/* =========================================================================
   Penny Premium 3D — gallery.js
   Product media gallery. One DOM, two behaviors:
   - Mobile: edge-to-edge scroll-snap swipe with a "2 / 6" counter.
   - Desktop (≥990px): stacked main media with soft opacity crossfade,
     driven by the thumbnail rail.
   Plus: tap-to-play video, deferred model-viewer (loads only on tap via
   Shopify.loadFeatures), and a <dialog> lightbox with keyboard nav,
   swipe-down close, double-tap and pinch zoom.
   Listens for 'variant:changed' (product-form.js) to jump to a variant's
   featured media.
   ========================================================================= */

const penny = window.penny;
const desktop = window.matchMedia('(min-width: 990px)');

function initGallery(root) {
  if (root.dataset.galleryReady) return;
  root.dataset.galleryReady = 'true';

  const track = root.querySelector('[data-gallery-track]');
  const items = Array.from(root.querySelectorAll('[data-gallery-item]'));
  const thumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
  const current = root.querySelector('[data-gallery-current]');
  const lightbox = root.querySelector('[data-gallery-lightbox]');
  // Start from the server-rendered active item; guarantee one exists so
  // the desktop stack can never render with every layer at opacity 0.
  let activeIndex = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));

  /* --- Active media ------------------------------------------------------ */
  const setActive = (index, scroll) => {
    activeIndex = Math.max(0, Math.min(index, items.length - 1));
    items.forEach((item, i) => item.classList.toggle('is-active', i === activeIndex));
    thumbs.forEach((thumb, i) => {
      thumb.setAttribute('aria-current', i === activeIndex ? 'true' : 'false');
      if (i === activeIndex) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    });
    if (current) current.textContent = String(activeIndex + 1);
    if (scroll && !desktop.matches) {
      track.scrollTo({ left: items[activeIndex].offsetLeft, behavior: 'auto' });
    }
  };

  // Mobile: derive the counter from scroll position
  let ticking = false;
  track.addEventListener(
    'scroll',
    () => {
      if (ticking || desktop.matches) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const width = items[0]?.getBoundingClientRect().width || 1;
        setActive(Math.round(track.scrollLeft / width), false);
      });
    },
    { passive: true }
  );

  thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => setActive(i, true)));
  setActive(activeIndex, false);

  // Variant → media sync
  penny.on('variant:changed', (event) => {
    const mediaId = event.detail?.variant?.featured_media?.id;
    if (!mediaId) return;
    const index = items.findIndex((item) => item.dataset.mediaId === String(mediaId));
    if (index > -1) setActive(index, true);
  });

  /* --- Deferred video / 3D model ----------------------------------------- */
  root.addEventListener('click', (event) => {
    const play = event.target.closest('[data-media-play]');
    if (!play) return;
    const item = play.closest('[data-gallery-item]');
    const template = item.querySelector('template');
    if (!template) return;

    const mount = () => {
      const node = template.content.cloneNode(true).firstElementChild;
      node.classList.add('gallery__mounted-media');
      item.querySelector('[data-media-poster]').replaceWith(node);
      play.remove();
      const video = item.querySelector('video');
      if (video) video.play().catch(() => {});
      const model = item.querySelector('model-viewer');
      if (model && window.Shopify?.ModelViewerUI) new window.Shopify.ModelViewerUI(model);
    };

    if (item.dataset.mediaType === 'model' && window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([
        { name: 'model-viewer-ui', version: '1.0', onLoad: mount },
      ]);
    } else {
      mount();
    }
  });

  /* --- Lightbox ----------------------------------------------------------
     <dialog> gives us native focus containment + Escape. We add arrow-key
     nav, swipe-down close, and double-tap / pinch zoom on the image. */
  if (!lightbox) return;

  const slides = Array.from(lightbox.querySelectorAll('[data-lightbox-slide]'));
  const lbCurrent = lightbox.querySelector('[data-lightbox-current]');
  let lbIndex = 0;
  let zoom = { scale: 1, x: 0, y: 0 };
  const pointers = new Map();
  let pinchStart = 0;
  let lastTap = 0;

  const activeImg = () => slides[lbIndex]?.querySelector('img');

  const applyZoom = () => {
    const img = activeImg();
    if (img) img.style.transform = `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`;
  };

  const resetZoom = () => {
    zoom = { scale: 1, x: 0, y: 0 };
    applyZoom();
  };

  const showSlide = (index) => {
    resetZoom();
    lbIndex = (index + slides.length) % slides.length;
    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === lbIndex));
    if (lbCurrent) lbCurrent.textContent = String(lbIndex + 1);
  };

  root.addEventListener('click', (event) => {
    const zoomTrigger = event.target.closest('[data-gallery-zoom]');
    if (!zoomTrigger) return;
    const item = zoomTrigger.closest('[data-gallery-item]');
    const index = slides.findIndex((slide) => slide.dataset.mediaId === item.dataset.mediaId);
    lightbox.showModal();
    showSlide(Math.max(index, 0));
  });

  lightbox.addEventListener('click', (event) => {
    if (event.target.closest('[data-lightbox-close]')) lightbox.close();
    if (event.target.closest('[data-lightbox-prev]')) showSlide(lbIndex - 1);
    if (event.target.closest('[data-lightbox-next]')) showSlide(lbIndex + 1);
    // Click on the backdrop (outside the figure) closes
    if (event.target === lightbox) lightbox.close();
  });

  lightbox.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') showSlide(lbIndex - 1);
    if (event.key === 'ArrowRight') showSlide(lbIndex + 1);
  });

  lightbox.addEventListener('close', resetZoom);

  /* Touch: pinch zoom, pan when zoomed, swipe-down close, double-tap zoom */
  lightbox.addEventListener('pointerdown', (event) => {
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinchStart = Math.hypot(a.x - b.x, a.y - b.y) / zoom.scale;
    }
  });

  lightbox.addEventListener('pointermove', (event) => {
    const prev = pointers.get(event.pointerId);
    if (!prev) return;
    const point = { x: event.clientX, y: event.clientY };

    if (pointers.size === 2) {
      pointers.set(event.pointerId, point);
      const [a, b] = [...pointers.values()];
      zoom.scale = Math.min(4, Math.max(1, Math.hypot(a.x - b.x, a.y - b.y) / pinchStart));
      applyZoom();
    } else if (zoom.scale > 1) {
      zoom.x += point.x - prev.x;
      zoom.y += point.y - prev.y;
      pointers.set(event.pointerId, point);
      applyZoom();
    }
  });

  lightbox.addEventListener('pointerup', (event) => {
    const start = pointers.get(event.pointerId);
    pointers.delete(event.pointerId);
    if (!start || pointers.size > 0) return;

    const dy = event.clientY - start.y;
    const dx = event.clientX - start.x;

    // Swipe down closes (only when not zoomed in)
    if (zoom.scale === 1 && dy > 80 && Math.abs(dx) < 60) {
      lightbox.close();
      return;
    }

    // Double tap toggles zoom
    if (event.pointerType === 'touch' && Math.abs(dy) < 10 && Math.abs(dx) < 10) {
      const now = Date.now();
      if (now - lastTap < 320) {
        zoom = zoom.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2.5, x: 0, y: 0 };
        applyZoom();
      }
      lastTap = now;
    }
  });

  lightbox.addEventListener('dblclick', (event) => {
    if (event.target.closest('button')) return;
    zoom = zoom.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2.5, x: 0, y: 0 };
    applyZoom();
  });
}

function initAll() {
  document.querySelectorAll('[data-gallery]').forEach(initGallery);
}

initAll();
document.addEventListener('shopify:section:load', initAll);
